import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { FaFilePdf } from 'react-icons/fa';
import { getProducts, getProjects } from '../lib/api';
import ImageCompare from '../components/ImageCompare'; // Fix the import statement for ImageCompare component

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, productsData] = await Promise.all([
          getProjects({ active: true, limit: 3 }),
          getProducts({ active: true, includeImages: true, limit: 4 }),
        ]);

        setFeaturedProjects(projectsData || []);
        setPopularProducts(productsData || []);
      } catch (error) {
        console.error('Veri çekme hatası:', error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <motion.section
        className="relative min-h-[90vh] flex flex-col lg:flex-row items-stretch overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Left Content Side */}
        <motion.div
          className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-[#1a1a1a] relative z-10"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="max-w-xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mb-6"
            >
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4">
                <span className="block text-[#dbc582]">Modern Yaşam</span>
                <span className="block">Alanları İçin</span>
                <span className="block">Yenilikçi Çözümler</span>
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed">
                Premium kalitede kompozit deck, ahşap deck ve veranda sistemleriyle
                yaşam alanlarınızı dönüştürüyoruz.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/products"
                className="group inline-flex items-center justify-center px-6 py-3 text-base font-medium text-[#1a1a1a] bg-[#dbc582] rounded-full transition-all duration-300 hover:bg-[#e5d194] hover:shadow-lg hover:shadow-[#dbc582]/20"
              >
                Ürünlerimizi Keşfedin
                <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white border-2 border-white/20 hover:border-white/40 rounded-full transition-all duration-300 hover:bg-white/10"
              >
                İletişime Geç
              </Link>
              <a
                href="/docs/kartela-katalog.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white border-2 border-[#dbc582]/50 hover:border-[#dbc582] rounded-full transition-all duration-300 hover:bg-[#dbc582]/10"
              >
                <FaFilePdf className="w-5 h-5 mr-2 text-[#dbc582]" />
                Kartela
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Image Side */}
        <motion.div
          className="flex-1 relative overflow-hidden min-h-[50vh] lg:min-h-full"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <motion.div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: 'url(/images/banner/banner.webp)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            animate={{
              scale: [1, 1.05],
              y: [0, -10],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          />

          {/* Floating Stats */}
          <div className="absolute bottom-8 left-8 right-8 flex justify-between gap-4 z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="bg-white/10 backdrop-blur-md rounded-xl p-4 flex-1"
            >
              <div className="text-2xl font-bold text-white">10+</div>
              <div className="text-sm text-gray-300">Yıllık Deneyim</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="bg-white/10 backdrop-blur-md rounded-xl p-4 flex-1"
            >
              <div className="text-2xl font-bold text-white">500+</div>
              <div className="text-sm text-gray-300">Tamamlanan Proje</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.section>

      {/* Partner Logos Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 items-center justify-items-center gap-8 md:gap-12 lg:gap-16"
          >
            {[
              { src: '/images/ado/ado-bayi-logo/adofloor.png', alt: 'Ado Floor' },
              { src: '/images/ado/ado-bayi-logo/adokapi.png', alt: 'Ado Kapı' },
              { src: '/images/ado/ado-bayi-logo/deckado.png', alt: 'Deckado' },
              { src: '/images/ado/ado-bayi-logo/vinylbond-logo-guncel.webp', alt: 'Vinylbond' },
            ].map((logo, index) => (
              <motion.div
                key={logo.alt}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="grayscale hover:grayscale-0 transition-all duration-300 transform hover:scale-110 flex items-center justify-center p-4 w-full"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-8 md:h-12 lg:h-16 w-auto object-contain max-w-full"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Kompozit Deck Bilgi Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Doğanın Estetiği, Dayanıklılıkla Buluşuyor
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kompozit Deck, FSC sertifikalı ahşap talaşı ve geri dönüştürülmüş plastikten üretilen çevre dostu bir zemin kaplama çözümüdür.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="prose prose-lg">
                <p className="text-gray-600">
                  %65 ahşap lifi, %30 polimer ve %5 katkı maddesi içeren bu yenilikçi malzeme, hem doğallığı hem de
                  modern tasarımı bir araya getirir. Zararlı maddeler içermez, güvenli ve sürdürülebilir yapısıyla hem konut hem de
                  ticari alanlarda kullanım için idealdir.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Dayanıklılık</h3>
                  <p className="text-gray-600">Çürüme, böceklenme ve çatlama gibi sorunlara karşı dayanıklıdır.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Hava Koşullarına Direnç</h3>
                  <p className="text-gray-600">Yağmur, kar, güneş gibi dış etkenlere karşı uzun ömürlüdür.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Bakım Kolaylığı</h3>
                  <p className="text-gray-600">Vernik veya boya gerektirmez, sadece su ve sabunla temizlenebilir.</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Çevre Dostu</h3>
                  <p className="text-gray-600">Geri dönüştürülmüş malzemelerden üretilir, sürdürülebilir bir seçimdir.</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[400px]"
            >
              <ImageCompare
                beforeImage="/images/photo-1.png"
                afterImage="/images/photo-2.png"
                alt="Kompozit Deck Karşılaştırma"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Öne Çıkan Projeler */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900">Öne Çıkan Projeler</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {featuredProjects.map((project) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-lg bg-gray-100"
              >
                <div className="aspect-w-4 aspect-h-3">
                  <img
                    src={project.images?.[0]}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-lg font-medium text-white">{project.title}</h3>
                  <div className="mt-2 flex items-center text-sm text-white/80">
                    <span>{project.location}</span>
                    <span className="mx-2">•</span>
                    <span>{new Date(project.completion_date).getFullYear()}</span>
                  </div>
                </div>
                <Link
                  to={`/projects/${project.id}`}
                  className="absolute inset-0"
                  aria-label={`${project.title} projesinin detaylarını gör`}
                />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/projects"
              className="inline-flex items-center justify-center px-5 py-2 text-base font-medium text-white bg-[#dbc582] hover:bg-[#c4b070] rounded-md transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Tüm Projeleri Görüntüle
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Popüler Ürünler */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900">Popüler Ürünler</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {popularProducts.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-lg bg-white"
              >
                <div className="aspect-w-1 aspect-h-1">
                  {product.product_images?.[0] && (
                    <img
                      src={product.product_images[0].image_url}
                      alt={`${product.title} - ${product.product_images[0].colors?.name || ''}`}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-medium text-gray-900">{product.title}</h3>
                  {product.product_images?.[0]?.colors && (
                    <p className="mt-1 text-sm text-gray-500">
                      {product.product_images[0].colors.name}
                    </p>
                  )}
                </div>
                <Link
                  to={`/products/${product.slug}`}
                  className="absolute inset-0"
                  aria-label={`${product.title} ürününün detaylarını gör`}
                />
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/products"
              className="inline-flex items-center justify-center px-5 py-2 text-base font-medium text-white bg-[#dbc582] hover:bg-[#c4b070] rounded-md transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Tüm Ürünleri Görüntüle
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Kompozit Deck Avantajları */}
      <motion.section
        className="py-20 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl font-semibold text-gray-900">Kompozit Deck Avantajları</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v20M2 12h20" />
                  <path d="M12 9v0" />
                  <path d="M12 15v0" />
                  <path d="M9 12h0" />
                  <path d="M15 12h0" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Genleşmez</h3>
              <p className="text-sm text-gray-600">Sıcaklık değişimlerinden etkilenmez</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Uzun Ömürlü</h3>
              <p className="text-sm text-gray-600">10 yıl garanti süresi</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
                  <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
                  <line x1="6" y1="1" x2="6" y2="4" />
                  <line x1="10" y1="1" x2="10" y2="4" />
                  <line x1="14" y1="1" x2="14" y2="4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Su Geçirmez</h3>
              <p className="text-sm text-gray-600">%100 su geçirmez özellik</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.29 7 12 12 20.71 7" />
                  <line x1="12" y1="22" x2="12" y2="12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Kolay Bakım</h3>
              <p className="text-sm text-gray-600">Minimum bakım gereksinimi</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">UV Dayanıklı</h3>
              <p className="text-sm text-gray-600">Güneş ışınlarına karşı koruma</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.29 7 12 12 20.71 7" />
                  <line x1="12" y1="22" x2="12" y2="12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Geri Dönüşümlü</h3>
              <p className="text-sm text-gray-600">Çevre dostu malzeme</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <line x1="10" y1="9" x2="8" y2="9" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sertifikalı</h3>
              <p className="text-sm text-gray-600">Uluslararası kalite standartları</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dbc582]/10 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#dbc582]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.29 7 12 12 20.71 7" />
                  <line x1="12" y1="22" x2="12" y2="12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Montaj Kolaylığı</h3>
              <p className="text-sm text-gray-600">Pratik klips sistemi</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Yaşam Alanlarınızı Yenileyin */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 mb-16">
            <div className="w-full md:w-2/5">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="relative aspect-[4/5]"
              >
                <img
                  src="/images/veranda-bg.jpg"
                  alt="Detay Deck Ürünleri"
                  className="w-full h-full object-cover rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 bg-black/10 rounded-2xl"></div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-full md:w-3/5 md:pl-8 max-w-xl"
            >
              <h2 className="text-2xl font-bold mb-6 text-gray-800">
                Detay Deck ile Yaşam Alanlarınızı Yenileyin
              </h2>
              <div className="space-y-4 text-sm leading-relaxed">
                <p className="text-gray-600">
                  2015 yılından bu yana, Detay Deck olarak Türkiye'nin önde gelen kompozit deck ve dış mekan kaplama malzemeleri tedarikçisiyiz. Geniş ürün yelpazemiz ve uzman ekibimizle, müşterilerimize en kaliteli ürünleri ve profesyonel hizmeti sunuyoruz.
                </p>
                <p className="text-gray-600">
                  İstanbul merkezli firmamız, Türkiye'nin her yerine hizmet vermektedir. Uzman montaj ekiplerimiz ve teknik danışmanlarımızla, projenizin her aşamasında yanınızdayız.
                </p>
                <p className="text-gray-600">
                  Detay Deck kalitesi ve güvencesiyle, dış mekan yaşam alanlarınızı baştan yaratıyoruz.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Newsletter Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden border border-[#dbc582]/20"
            >
              <div className="p-8">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                    Gelişmelerden Haberdar Olun
                  </h3>
                  <p className="mb-6 text-gray-600">
                    En son haberler, özel teklifler ve yeni ürünlerimizden ilk siz haberdar olun.
                  </p>
                  <form className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="email"
                      placeholder="E-posta adresiniz"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#dbc582] focus:border-transparent"
                    />
                    <button
                      type="submit"
                      className="px-6 py-3 bg-[#dbc582] text-white font-medium rounded-md hover:bg-[#c4b070] transition-colors duration-300 whitespace-nowrap"
                    >
                      Abone Ol
                    </button>
                  </form>
                  <p className="text-sm text-gray-500 mt-4">
                    Gizlilik politikamıza göre bilgileriniz güvende.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Visit Box */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gray-800 rounded-2xl overflow-hidden relative group"
            >
              <img
                src="/images/category-1.jpg"
                alt="Showroom"
                className="w-full h-full object-cover absolute inset-0 group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/50"></div>
              <div className="relative z-10 p-8 h-full flex flex-col justify-between">
                <div>
                  <h3 className="text-2xl font-semibold mb-4 text-white">Showroom'umuzu Ziyaret Edin</h3>
                  <p className="text-white/90 mb-6">
                    Ürünlerimizi yakından incelemek ve uzman ekibimizle tanışmak için showroom'umuza davetlisiniz.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://maps.app.goo.gl/48rYKxgSwjHRq33j9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-[#dbc582] bg-white hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Yol Tarifi Al
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#dbc582] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Özel Teklifler İçin Bize Ulaşın</h2>
          <p className="mb-8 max-w-2xl mx-auto">
            Size özel fiyatlar ve profesyonel danışmanlık hizmeti için hemen iletişime geçin.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#dbc582] px-6 py-3 rounded-md font-semibold hover:bg-gray-50 transition-colors"
          >
            İletişime Geçin
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
